import React, { useState, useRef, useEffect } from "react";
import {
  Mic,
  Square,
  Download,
  Upload,
  FileAudio,
  Loader2,
  ArrowLeft,
  Headphones,
  AlertCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

const HybridAudioRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [duration, setDuration] = useState(0); // 웹M 파일 재생 시간 (초)
  const [error, setError] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const recordStartTimeRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { api } = useAuth();

  // 녹음이 멈추었을 때 Blob 생성 및 URL 설정
  const handleStopRecording = () => {
    if (recordStartTimeRef.current) {
      const elapsedMs = Date.now() - recordStartTimeRef.current;
      setDuration(elapsedMs / 1000);
    }
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    setRecordedBlob(blob);
    const url = URL.createObjectURL(blob);
    setAudioUrl(url);
  };

  // audioUrl이 바뀔 때마다 Audio 객체로 duration 계산
  useEffect(() => {
    if (!audioUrl) {
      setDuration(0);
      return;
    }
    const audio = new Audio();
    audio.src = audioUrl;
    const onLoaded = () => {
      setDuration(audio.duration);
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.remove();
    };
    audio.addEventListener("loadedmetadata", onLoaded);
    audio.preload = "metadata";
    if (uploadedFile) {
      const audio = new Audio();
      audio.src = audioUrl;
      const onLoaded = () => {
        setDuration(audio.duration);
        audio.removeEventListener("loadedmetadata", onLoaded);
        audio.remove();
      };
      audio.addEventListener("loadedmetadata", onLoaded);
      audio.preload = "metadata";
    }
    // return () => {
    //   audio.removeEventListener("loadedmetadata", onLoaded);
    // };
    return;
  }, [audioUrl, uploadedFile]);

  // 1) 녹음 시작
  const startRecording = async () => {
    try {
      setError("");
      chunksRef.current = [];
      setRecordedBlob(null);
      setUploadedFile(null);
      setAudioUrl(null);
      setDuration(0);

      recordStartTimeRef.current = Date.now();

      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          sampleRate: 44100,
        },
      });
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });

      const audioContext = new AudioContext({ sampleRate: 44100 });
      const destination = audioContext.createMediaStreamDestination();

      if (displayStream.getAudioTracks().length > 0) {
        const systemSource = audioContext.createMediaStreamSource(
          new MediaStream(displayStream.getAudioTracks())
        );
        const systemGain = audioContext.createGain();
        systemGain.gain.value = 1.0;
        systemSource.connect(systemGain);
        systemGain.connect(destination);
      }

      const micSource = audioContext.createMediaStreamSource(micStream);
      const micGain = audioContext.createGain();
      micGain.gain.value = 1.0;
      micSource.connect(micGain);
      micGain.connect(destination);

      const combinedStream = destination.stream;
      const mediaRecorder = new MediaRecorder(combinedStream, {
        mimeType: "audio/webm",
        audioBitsPerSecond: 32_000,
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      mediaRecorder.onstop = () => {
        handleStopRecording();
        displayStream.getTracks().forEach((t) => t.stop());
        micStream.getTracks().forEach((t) => t.stop());
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);

      displayStream.getVideoTracks().forEach((t) => t.stop());
    } catch (err) {
      console.error("녹음 시작 실패:", err);
      setError("오디오 캡처를 취소하였습니다.");
    }
  };

  // 2) 녹음 중지
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // 3) 녹음 다운로드
  const downloadRecording = () => {
    if (audioUrl) {
      const a = document.createElement("a");
      a.href = audioUrl;
      a.download = `meeting.webm`;
      a.click();
    }
  };

  // 4) 파일 업로드 선택
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && (file.type.includes("audio") || file.type.includes("video"))) {
      setUploadedFile(file);
      setRecordedBlob(null);
      const url = URL.createObjectURL(file);
      setAudioUrl(url);
      setDuration(0);
      setError("");
    } else {
      setError("오디오 또는 비디오 파일만 업로드 가능합니다.");
    }
  };

  // 5) AI 분석 시작 (Go 서버로 업로드)
  const startAnalysis = async () => {
    try {
      setError("");
      if (!recordedBlob && !uploadedFile) {
        setError("먼저 녹음하거나 파일을 업로드해 주세요.");
        return;
      }
      setIsAnalyzing(true);

      const formData = new FormData();
      if (recordedBlob) {
        formData.append("file", recordedBlob, "recording.webm");
      } else if (uploadedFile) {
        formData.append("file", uploadedFile, uploadedFile.name);
      }
      // duration도 함께 전송하려면:
      formData.append("duration", duration.toString());

      const res = await api.post("/meeting/analyze", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      console.log(res);
    } catch (e) {
      console.error("분석 요청 실패:", e);
      setError("분석 중 오류가 발생했습니다.");
      setIsAnalyzing(false);
    }
  };

  if (isAnalyzing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="mb-8">
            <Loader2 className="w-16 h-16 text-teal-600 animate-spin mx-auto" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            회의 내용을 분석하고 있습니다
          </h2>
          <p className="text-gray-600 max-w-md mx-auto">
            AI가 회의 내용을 분석하여 요약본을 생성하고 있습니다.
            <br />
            최대 15분 정도 소요될 수 있습니다.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg font-medium hover:shadow-lg transition-all duration-300"
          >
            돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-white">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">돌아가기</span>
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">회의 녹음</h1>
        <p className="text-gray-600 mb-8">
          회의를 녹음하거나 파일을 업로드하여 AI 분석을 받아보세요
        </p>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Recording Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Headphones className="w-10 h-10 text-teal-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                실시간 녹음
              </h2>
              <p className="text-sm text-gray-600">
                화면 공유를 통해 회의를 녹음합니다
              </p>
            </div>

            {!isRecording ? (
              <button
                onClick={startRecording}
                className="w-full py-3 px-4 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl hover:shadow-md transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Mic className="w-5 h-5" />
                <span className="font-medium">녹음 시작하기</span>
              </button>
            ) : (
              <div>
                <button
                  onClick={stopRecording}
                  className="w-full py-3 px-4 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors flex items-center justify-center gap-2 mb-3"
                >
                  <Square className="w-4 h-4" />
                  <span className="font-medium">녹음 중지</span>
                </button>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  녹음 중...
                </div>
              </div>
            )}
          </div>

          {/* Upload Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <Upload className="w-10 h-10 text-gray-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                파일 업로드
              </h2>
              <p className="text-sm text-gray-600">
                이미 녹음된 파일이 있다면 업로드하세요
              </p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*,video/*"
              onChange={handleFileUpload}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full py-3 px-4 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
            >
              <Upload className="w-5 h-5" />
              <span className="font-medium">파일 선택하기</span>
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Audio Preview & Analysis */}
        {audioUrl && (
          <div className="mt-6 bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <FileAudio className="w-5 h-5 text-teal-600" />
              <h3 className="font-medium text-gray-900">
                {uploadedFile ? uploadedFile.name : "녹음 파일"} (
                {duration.toFixed(2)}초)
              </h3>
            </div>

            <audio controls src={audioUrl} className="w-full mb-4" />

            <div className="flex gap-3">
              {!uploadedFile && (
                <button
                  onClick={downloadRecording}
                  className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Download className="w-4 h-4" />
                  다운로드
                </button>
              )}
              <button
                onClick={startAnalysis}
                className="flex-1 py-2 px-4 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-lg hover:shadow-md transition-all duration-200 font-medium"
              >
                AI 분석 시작
              </button>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-8 bg-gray-50 rounded-2xl p-6">
          <h3 className="font-medium text-gray-900 mb-3">💡 사용 가이드</h3>
          <div className="space-y-2 text-sm text-gray-600">
            <p>• 줌, 구글 미팅, 디스코드 등 모든 화상회의 플랫폼 지원</p>
            <p>
              • 녹음 시작 → 화면 공유 창에서 회의 앱 선택 → "시스템 오디오 공유"
              체크
            </p>
            <p>• 본인 목소리와 상대방 목소리가 모두 녹음됩니다</p>
            <p>• 녹음 파일은 WebM 형식으로 저장됩니다</p>
          </div>
        </div>

        {/* Warning Notice */}
        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
          <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            주의사항
          </h3>
          <div className="space-y-2 text-sm text-gray-700">
            <p className="font-medium">
              • 현재 Preview 버전으로 30분 이내의 회의만 지원됩니다
            </p>
            <p>• 30분을 초과하는 회의는 정식 버전에서 지원 예정입니다</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HybridAudioRecorder;
